import React, { useState, useRef, useEffect } from 'react';
import {
  getAIConfig,
  getAIUsage,
  getExplanation,
  getWorkedExample,
  sendDialogueMessage,
  AIConfig,
  DialogueMessage,
} from '../services/api';
import { MathRenderer } from './shared/MathRenderer';
import { storage } from '../services/storage';

interface AIHelpPanelProps {
  questionId: string;
  studentId: string;
  studentAnswer?: string;
  showAfterResult?: boolean;
  onClose?: () => void;
  questionPrompt?: string;
  correctAnswer?: string;
}

type AIMode = 'explain' | 'worked-example' | 'dialogue';

export const AIHelpPanel: React.FC<AIHelpPanelProps> = ({
  questionId,
  studentId,
  studentAnswer,
  showAfterResult = false,
  onClose,
  questionPrompt,
  correctAnswer,
}) => {
  const [config, setConfig] = useState<AIConfig | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [mode, setMode] = useState<AIMode | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiUsage, setAiUsage] = useState<{ used: number; limit: number } | null>(null);
  const [rateLimited, setRateLimited] = useState(false);

  // Content state
  const [explanation, setExplanation] = useState<string | null>(null);
  const [workedExample, setWorkedExample] = useState<string | null>(null);

  // Dialogue state
  const [dialogueHistory, setDialogueHistory] = useState<DialogueMessage[]>([]);
  const [dialogueInput, setDialogueInput] = useState('');
  const dialogueEndRef = useRef<HTMLDivElement>(null);

  // First-time callout
  const [showFirstTimeCallout, setShowFirstTimeCallout] = useState(false);

  // Fetch AI config and usage on mount
  useEffect(() => {
    setConfigLoading(true);
    Promise.all([
      getAIConfig().catch((err) => {
        console.error('Failed to fetch AI config:', err);
        return null;
      }),
      getAIUsage(studentId).catch(() => null),
    ]).then(([cfg, usage]) => {
      setConfig(cfg);
      if (usage) {
        setAiUsage(usage);
        setRateLimited(usage.used >= usage.limit);
      }
      setConfigLoading(false);
    });
  }, [studentId]);

  // Scroll dialogue to bottom on new messages
  useEffect(() => {
    if (dialogueEndRef.current) {
      dialogueEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [dialogueHistory]);

  // Show first-time callout when AI becomes available
  useEffect(() => {
    if (config && !rateLimited && !storage.hasSeenTip('ai_tutor')) {
      setShowFirstTimeCallout(true);
    }
  }, [config, rateLimited]);

  // While loading config, don't render anything
  if (configLoading) {
    return null;
  }

  // If AI is not enabled or config failed to load, don't render
  if (!config?.enabled) {
    return null;
  }

  const handleAIError = (err: any, fallbackMsg: string) => {
    if (err.response?.status === 429) {
      setRateLimited(true);
      setError("You've reached your daily AI tutor limit (30). It resets tomorrow.");
    } else {
      setError(err.response?.data?.detail || fallbackMsg);
    }
  };

  const refreshUsage = () => {
    getAIUsage(studentId).then(setAiUsage).catch(() => {});
  };

  const handleExplain = async () => {
    if (!config.features.explain) return;

    setMode('explain');
    setLoading(true);
    setError(null);

    try {
      const response = await getExplanation(questionId, studentId, studentAnswer, questionPrompt, correctAnswer);
      setExplanation(response.explanation);
      refreshUsage();
    } catch (err: any) {
      handleAIError(err, 'Failed to get explanation');
    } finally {
      setLoading(false);
    }
  };

  const handleWorkedExample = async () => {
    if (!config.features.worked_examples) return;

    setMode('worked-example');
    setLoading(true);
    setError(null);

    try {
      const response = await getWorkedExample(questionId, studentId, questionPrompt, correctAnswer);
      setWorkedExample(response.worked_example);
      refreshUsage();
    } catch (err: any) {
      handleAIError(err, 'Failed to get worked example');
    } finally {
      setLoading(false);
    }
  };

  const startDialogue = () => {
    if (!config.features.socratic) return;
    setMode('dialogue');
    setDialogueHistory([]);
  };

  const sendDialogue = async () => {
    if (!dialogueInput.trim()) return;

    const userMessage = dialogueInput.trim();
    setDialogueInput('');
    setLoading(true);
    setError(null);

    try {
      const response = await sendDialogueMessage(
        questionId,
        studentId,
        userMessage,
        dialogueHistory,
        questionPrompt,
        correctAnswer,
        studentAnswer,
      );
      setDialogueHistory(response.conversation_history);
      refreshUsage();
    } catch (err: any) {
      handleAIError(err, 'Failed to send message');
      // Add the user message back to input if failed
      setDialogueInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetPanel = () => {
    setMode(null);
    setExplanation(null);
    setWorkedExample(null);
    setDialogueHistory([]);
    setError(null);
  };

  // Button panel (before selecting a mode)
  if (!mode) {
    const remaining = aiUsage ? aiUsage.limit - aiUsage.used : null;

    return (
      <div className="ai-help-panel">
        <div className="ai-help-header">
          <span className="ai-badge">AI Tutor</span>
          {remaining !== null && (
            <span className="ai-usage">{remaining} / {aiUsage!.limit} remaining today</span>
          )}
        </div>
        {rateLimited ? (
          <div className="ai-error">
            You've reached your daily AI tutor limit (30). It resets tomorrow.
          </div>
        ) : (
          <div className="ai-help-buttons">
            {showFirstTimeCallout && (
              <div className="ai-first-time-callout">
                <button
                  className="callout-close"
                  onClick={() => {
                    storage.markTipSeen('ai_tutor');
                    setShowFirstTimeCallout(false);
                  }}
                >×</button>
                <p>
                  Need help? The AI tutor won't give you the answer — it'll help you
                  figure it out yourself.
                </p>
              </div>
            )}
            {config.features.explain && showAfterResult && (
              <button className="ai-btn explain-btn" onClick={handleExplain}>
                <span className="ai-btn-icon">📖</span>
                Explain This
              </button>
            )}
            {config.features.worked_examples && showAfterResult && (
              <button className="ai-btn example-btn" onClick={handleWorkedExample}>
                <span className="ai-btn-icon">📝</span>
                Worked Example
              </button>
            )}
            {config.features.socratic && (
              <button className="ai-btn dialogue-btn" onClick={startDialogue}>
                <span className="ai-btn-icon">💬</span>
                Talk to Tutor
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Content panel (after selecting a mode)
  return (
    <div className="ai-help-panel ai-help-expanded">
      <div className="ai-help-header">
        <span className="ai-badge">AI Tutor</span>
        <button className="ai-back-btn" onClick={resetPanel}>
          ← Back
        </button>
        {onClose && (
          <button className="ai-close-btn" onClick={onClose}>
            ×
          </button>
        )}
      </div>

      {loading && (
        <div className="ai-loading">
          <div className="ai-loading-spinner"></div>
          <span>Thinking...</span>
        </div>
      )}

      {error && (
        <div className="ai-error">
          <span className="error-icon">⚠️</span>
          {error}
        </div>
      )}

      {/* Explain Mode */}
      {mode === 'explain' && explanation && (
        <div className="ai-content explain-content">
          <h4>Explanation</h4>
          <div className="ai-text"><MathRenderer content={explanation} /></div>
        </div>
      )}

      {/* Worked Example Mode */}
      {mode === 'worked-example' && workedExample && (
        <div className="ai-content example-content">
          <h4>Worked Example</h4>
          <div className="ai-text"><MathRenderer content={workedExample} /></div>
        </div>
      )}

      {/* Dialogue Mode */}
      {mode === 'dialogue' && (
        <div className="ai-content dialogue-content">
          <h4>Chat with Tutor</h4>
          <div className="dialogue-messages">
            {dialogueHistory.length === 0 && !loading && (
              <div className="dialogue-intro">
                Hi! I'm here to help you understand this problem.
                Ask me anything about the question, and I'll guide you step by step.
              </div>
            )}
            {dialogueHistory.map((msg, idx) => (
              <div key={idx} className={`dialogue-message ${msg.role}`}>
                <span className="message-role">
                  {msg.role === 'student' ? 'You' : 'Tutor'}
                </span>
                <div className="message-content"><MathRenderer content={msg.content} /></div>
              </div>
            ))}
            <div ref={dialogueEndRef} />
          </div>
          <div className="dialogue-input-area">
            <input
              type="text"
              value={dialogueInput}
              onChange={(e) => setDialogueInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !loading) {
                  e.preventDefault();
                  e.stopPropagation();
                  sendDialogue();
                }
              }}
              placeholder="Type your question..."
              disabled={loading}
            />
            <button
              className="dialogue-send-btn"
              onClick={sendDialogue}
              disabled={loading || !dialogueInput.trim()}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIHelpPanel;
